import * as React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Button } from "./button";

interface ContextModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "7xl";
  className?: string;
}

export function ContextModal({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = "md",
  className = ""
}: ContextModalProps) {
  // Keypress event handler for Escape key
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    if (isOpen) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    "7xl": "max-w-7xl md:w-[92vw]"
  };

  const maxHeighClass = size === "7xl" ? "max-h-[90vh]" : "max-h-[85vh]";

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div 
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 overflow-y-auto animate-in fade-in duration-200"
      onClick={handleOverlayClick}
    >
      <div 
        className={`w-full bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col my-8 animate-in zoom-in-95 duration-200 border border-gray-100 ${maxHeighClass} ${sizeClasses[size]} ${className}`}
      >
        {/* HEADER */}
        {(title || description || onClose) && (
          <div className="flex items-start justify-between border-b border-gray-100 p-5 shrink-0 bg-gray-50/50">
            <div>
              {title && <h3 className="text-base font-bold text-gray-900 leading-tight">{title}</h3>}
              {description && <p className="text-xs font-semibold text-gray-500 mt-0.5">{description}</p>}
            </div>
            {onClose && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={onClose}
                className="h-8 w-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg shrink-0 ml-4 -mt-1 -mr-1"
              >
                <X className="w-4.5 h-4.5" />
              </Button>
            )}
          </div>
        )}

        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto p-6">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
